/**
 * Redux middleware — journey-log safety net for onboarding API failures.
 *
 * Phase 6 of public/ONBOARDING_LOGGING_PLAN.md asks for API_ERROR logging to
 * be wired into every onboarding *_ERR redux action, not just the three
 * complex screens (Connect PMS, Coverage, Review Guidebook) that already log
 * their own API_REQUEST/API_SUCCESS/API_ERROR manually with a requestId and
 * rich metadata. Hand-editing every one of the ~30 onboarding thunks in
 * actionCreator.js would work too, but this middleware covers all of them —
 * including every static/simple screen that has no manual instrumentation —
 * from one place, with no risk of missing one.
 *
 * This is intentionally observational only:
 *   - next(action) always runs first — this middleware can never change what
 *     any reducer or component sees, and can never block or delay dispatch.
 *   - Only action types matching ONBOARDING_*_ERR are inspected; every other
 *     action in the app (including the rest of the admin panel, unrelated to
 *     onboarding) passes through completely untouched.
 *   - ONBOARDING_LOGS_ERR itself is excluded, so a failing log-send can never
 *     trigger another log-send attempt (no infinite loop).
 *   - A few *_ERR types are excluded because the step file dispatching them
 *     already logs a richer API_ERROR manually (with requestId + specific
 *     metadata) right at the call site — letting this middleware log them
 *     too would just create a near-duplicate row for the same failure.
 */
import logEvent from './logEvent';
import { LOG_EVENT_TYPE, LOG_STATUS } from './eventTypes';
import currentScreenSlug from './currentScreenSlug';

const EXCLUDED_ERR_TYPES = new Set([
  'ONBOARDING_LOGS_ERR',                    // would log its own send failure — infinite loop risk
  'ONBOARDING_CONNECT_PMS_CREDENTIALS_ERR', // already logged manually in ConnectPmsStep (requestId + pms)
  'ONBOARDING_PMS_AUTH_ERR',                // already logged manually in ConnectPmsStep (requestId + pms)
  'ONBOARDING_STRIPE_SETUP_ERR',            // already logged manually in CoverageTiersStep (requestId + tier)
]);

// "ONBOARDING_STEP_SAVE_ERR" -> "STEP_SAVE" — a readable action name for the log.
const toActionName = (type) => type.replace(/^ONBOARDING_/, '').replace(/_ERR$/, '');

const onboardingApiErrorMiddleware = () => (next) => (action) => {
  const result = next(action);

  try {
    const type = action?.type;
    if (
      typeof type === 'string' &&
      type.startsWith('ONBOARDING_') &&
      type.endsWith('_ERR') &&
      !EXCLUDED_ERR_TYPES.has(type)
    ) {
      const err = action.err;
      logEvent(currentScreenSlug(), LOG_EVENT_TYPE.API_ERROR, {
        action: toActionName(type),
        status: LOG_STATUS.FAILED,
        errorMessage: typeof err === 'string' ? err : (err?.message ?? null),
      });
    }
  } catch {
    // Logging must never affect the actual redux dispatch — swallow.
  }

  return result;
};

export default onboardingApiErrorMiddleware;
